import React, { useState, useEffect, useCallback } from 'react';
import {
  MessageSquareText, Send, Users, BarChart3, Settings2, Zap, Plus,
  Loader2, RefreshCw, CheckCircle, XCircle, Clock, ChevronLeft, ChevronRight,
  Search, Trash2, Wallet, CreditCard, Calendar, ToggleLeft, ToggleRight,
  AlertCircle, Info, GraduationCap, Users2, Phone
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api';
import SMSContactsTab from '../../components/sms/SMSContactsTab';

const TABS = [
  { id: 'dashboard',   label: 'Dashboard',    icon: BarChart3 },
  { id: 'send',        label: 'Send SMS',      icon: Send },
  { id: 'contacts',    label: 'Contacts',      icon: Users },
  { id: 'campaigns',   label: 'Campaigns',     icon: MessageSquareText },
  { id: 'automations', label: 'Automations',   icon: Zap },
  { id: 'wallet',      label: 'Wallet',        icon: Wallet },
  { id: 'settings',    label: 'Settings',      icon: Settings2 },
];

const STATUS_COLORS = {
  sent:      'bg-blue-100 text-blue-700',
  scheduled: 'bg-violet-100 text-violet-700',
  sending:   'bg-amber-100 text-amber-700',
  failed:    'bg-red-100 text-red-700',
};

function StatCard({ label, value, sub, color, icon: Icon }) {
  const colors = { blue:'from-blue-500 to-blue-600', emerald:'from-emerald-500 to-emerald-600',
                   red:'from-red-500 to-red-600', violet:'from-violet-500 to-violet-600',
                   amber:'from-amber-500 to-amber-600', teal:'from-teal-500 to-teal-600' };
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 flex items-start gap-4">
      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${colors[color]||colors.blue} flex items-center justify-center flex-shrink-0`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div>
        <p className="text-xs font-bold text-slate-400 uppercase tracking-wide">{label}</p>
        <p className="text-2xl font-extrabold text-slate-900 mt-0.5">{value ?? '—'}</p>
        {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

// ── Access Gate ──────────────────────────────────────────────────────────
function AccessGate({ status, onRequest }) {
  const [requesting, setRequesting] = useState(false);

  const handleRequest = async () => {
    setRequesting(true);
    try {
      await onRequest();
    } finally { setRequesting(false); }
  };

  if (status === 'pending') {
    return (
      <div className="flex flex-col items-center justify-center py-20 space-y-4">
        <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center">
          <Clock className="w-8 h-8 text-amber-500" />
        </div>
        <h2 className="text-xl font-bold text-slate-800">Request Submitted</h2>
        <p className="text-slate-500 text-center max-w-md">Your SMS Platform access request is pending approval from the Master Admin. You'll be notified once approved.</p>
      </div>
    );
  }

  if (status === 'rejected') {
    return (
      <div className="flex flex-col items-center justify-center py-20 space-y-4">
        <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
          <XCircle className="w-8 h-8 text-red-500" />
        </div>
        <h2 className="text-xl font-bold text-slate-800">Request Rejected</h2>
        <p className="text-slate-500 text-center max-w-md">Your previous request was rejected. You may submit a new request.</p>
        <button onClick={handleRequest} disabled={requesting} className="px-6 py-3 bg-[#2563eb] text-white rounded-xl font-bold hover:bg-blue-700 transition-all flex items-center gap-2">
          {requesting ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Re-apply for Access
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center py-20 space-y-6 max-w-md mx-auto text-center">
      <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-blue-500 to-violet-600 flex items-center justify-center shadow-lg">
        <MessageSquareText className="w-10 h-10 text-white" />
      </div>
      <div>
        <h2 className="text-2xl font-extrabold text-slate-800">SMS Platform</h2>
        <p className="text-slate-500 mt-2">Send bulk SMS to students, parents and staff. Set up automated birthday and fee reminder messages.</p>
      </div>
      <div className="bg-slate-50 rounded-2xl p-4 w-full text-left space-y-2">
        <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">Features included</p>
        {['Bulk SMS to parents/students by class','Custom sender name','Birthday & fee reminder automation','Credit wallet with Paystack topup','Delivery analytics'].map(f => (
          <div key={f} className="flex items-center gap-2 text-sm text-slate-700">
            <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
            {f}
          </div>
        ))}
      </div>
      <button onClick={handleRequest} disabled={requesting}
        className="w-full py-3.5 rounded-xl bg-[#2563eb] text-white font-bold text-lg hover:bg-blue-700 transition-all flex items-center justify-center gap-2"
        data-testid="request-sms-access-btn">
        {requesting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
        Request SMS Access
      </button>
      <p className="text-xs text-slate-400">Access is free to request. Credits are purchased separately via your wallet.</p>
    </div>
  );
}

// ── DASHBOARD TAB ────────────────────────────────────────────────────────
function DashboardTab({ accessData }) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get('/school-sms/analytics').then(r => setStats(r.data)).catch(() => {});
  }, []);

  const balance = accessData?.wallet?.balance ?? 0;
  const pricePerSms = accessData?.pricing?.price_per_sms_naira ?? 0.25;
  const rate = stats?.total_sent ? Math.round((stats.total_delivered / stats.total_sent) * 100) : 0;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard label="SMS Credits" value={balance} icon={Wallet} color="violet" sub={`≈ ₦${(balance * pricePerSms).toFixed(2)} value`} />
        <StatCard label="Total Sent" value={stats?.total_sent ?? 0} icon={Send} color="blue" sub="All time" />
        <StatCard label="Delivered" value={stats?.total_delivered ?? 0} icon={CheckCircle} color="emerald" sub={`${rate}% delivery rate`} />
        <StatCard label="Failed" value={stats?.total_failed ?? 0} icon={XCircle} color="red" />
        <StatCard label="Campaigns" value={stats?.total_campaigns ?? 0} icon={MessageSquareText} color="amber" />
        <StatCard label="Credits Used" value={stats?.total_credits ?? 0} icon={Zap} color="teal" />
      </div>

      {balance === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-bold text-amber-800 text-sm">No SMS credits</p>
            <p className="text-xs text-amber-700 mt-0.5">Top up your wallet to start sending SMS. Go to the Wallet tab.</p>
          </div>
        </div>
      )}
    </div>
  );
}

// ── SEND SMS TAB ─────────────────────────────────────────────────────────
function SendSMSTab({ accessData }) {
  const [form, setForm]       = useState({ message: '', campaign_name: '', recipient_type: 'contacts', schedule_at: '', target: 'parents' });
  const [contacts, setContacts] = useState([]);
  const [classes, setClasses]   = useState([]);
  const [groups, setGroups]     = useState([]);
  const [selected, setSelected] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [manualNums, setManualNums] = useState('');
  const [sending, setSending]   = useState(false);

  useEffect(() => {
    api.get('/school-sms/contacts?limit=100&page=1').then(r => setContacts(r.data || [])).catch(() => {});
    api.get('/school-sms/classes').then(r => setClasses(r.data || [])).catch(() => {});
    api.get('/school-sms/groups').then(r => setGroups(r.data || [])).catch(() => {});
  }, []);

  const senderName = accessData?.sender_name || 'Eduva SMS';
  const selGroup   = groups.find(g => g.id === selectedGroup);
  const recipientCount = form.recipient_type === 'contacts' ? selected.length
    : form.recipient_type === 'classes'
    ? classes.filter(c => selected.includes(c.class_name)).reduce((acc, c) => acc + (form.target === 'parents' ? c.parent_phone_count : c.student_count), 0)
    : form.recipient_type === 'group' ? '?'
    : manualNums.split('\n').filter(l => l.trim()).length;

  const charCount = form.message.length;
  const smsCount  = Math.ceil(charCount / 160) || 1;
  const balance   = accessData?.wallet?.balance ?? 0;
  const totalUnits = form.recipient_type === 'group' ? '?' : smsCount * recipientCount;
  const canAfford = form.recipient_type === 'group' ? true : balance >= totalUnits;

  const handleSend = async () => {
    if (!form.message.trim()) return toast.error('Message is required');
    if (form.recipient_type === 'group' && !selectedGroup) return toast.error('Select a group');
    if (form.recipient_type !== 'group' && recipientCount === 0) return toast.error('Select at least one recipient');
    if (!canAfford) return toast.error(`Insufficient credits. Need ${totalUnits}, have ${balance}`);

    setSending(true);
    try {
      const payload = {
        message: form.message,
        campaign_name: form.campaign_name || undefined,
        schedule_at: form.schedule_at || undefined,
        recipient_type: form.recipient_type,
        contact_ids: form.recipient_type === 'contacts' ? selected : [],
        class_names: form.recipient_type === 'classes' ? selected : [],
        group_id: form.recipient_type === 'group' ? selectedGroup : undefined,
        target: form.target,
        manual_numbers: form.recipient_type === 'manual' ? manualNums.split('\n').filter(l => l.trim()) : [],
      };
      const r = await api.post('/school-sms/send', payload);
      toast.success(r.message || 'SMS sent!');
      setForm(f => ({ ...f, message: '', campaign_name: '', schedule_at: '' }));
      setSelected([]); setSelectedGroup(''); setManualNums('');
      api.get('/school-sms/access-status').then(() => {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send');
    } finally { setSending(false); }
  };

  const toggle = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
      <div className="lg:col-span-3 space-y-4">
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-slate-800">Compose Message</h3>
            <div className="flex items-center gap-2 text-xs">
              <Wallet className="w-3.5 h-3.5 text-slate-400" />
              <span className={`font-bold ${canAfford ? 'text-emerald-600' : 'text-red-500'}`}>{balance} credits</span>
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Campaign Name <span className="font-normal text-slate-400">(optional)</span></label>
            <input value={form.campaign_name} onChange={e => setForm(f => ({...f, campaign_name: e.target.value}))}
              placeholder="e.g. Term 1 Fee Reminder" className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-blue-400" />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Sender Name</label>
            <div className="px-4 py-2.5 border border-slate-100 rounded-xl bg-slate-50 text-sm text-slate-600 font-mono">{senderName}</div>
            <p className="text-xs text-slate-400 mt-1">Change in Settings tab</p>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Message</label>
            <textarea value={form.message} onChange={e => setForm(f => ({...f, message: e.target.value}))}
              rows={5} placeholder="Type your message here…"
              className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-blue-400 resize-none" />
            <div className="flex justify-between mt-1">
              <span className="text-xs text-slate-400">{charCount} chars</span>
              <span className={`text-xs font-semibold ${canAfford ? 'text-emerald-600' : 'text-red-500'}`}>
                {totalUnits} credits needed ({balance} available)
              </span>
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Schedule <span className="font-normal text-slate-400">(optional)</span></label>
            <input type="datetime-local" value={form.schedule_at}
              onChange={e => setForm(f => ({...f, schedule_at: e.target.value}))}
              className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-blue-400" />
          </div>

          <button onClick={handleSend} disabled={sending || !form.message.trim() || (!canAfford && form.recipient_type !== 'group')}
            className="w-full py-3 rounded-xl font-bold text-white bg-[#2563eb] hover:bg-blue-700 disabled:opacity-40 transition-all flex items-center justify-center gap-2"
            data-testid="school-send-sms-btn">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {form.schedule_at ? 'Schedule SMS' : form.recipient_type === 'group' && selGroup ? `Send to "${selGroup.name}"` : `Send to ${recipientCount} recipient${recipientCount !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>

      {/* Recipients panel */}
      <div className="lg:col-span-2 space-y-4">
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-5 space-y-3">
          <h3 className="font-bold text-slate-800">Recipients</h3>
          <div className="flex rounded-xl bg-slate-100 p-1 gap-1 flex-wrap">
            {[['contacts','Contacts'],['group','Group'],['classes','By Class'],['manual','Manual']].map(([v,l]) => (
              <button key={v} onClick={() => { setForm(f => ({...f, recipient_type: v})); setSelected([]); setSelectedGroup(''); }}
                className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-all ${form.recipient_type===v?'bg-white shadow text-slate-800':'text-slate-500'}`}>
                {l}
              </button>
            ))}
          </div>

          {form.recipient_type === 'group' && (
            <div className="space-y-2">
              <select value={selectedGroup} onChange={e => setSelectedGroup(e.target.value)}
                className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-blue-400"
                data-testid="school-group-select">
                <option value="">— Select a group —</option>
                {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
              {selectedGroup && (
                <p className="text-xs text-blue-600 bg-blue-50 rounded-lg px-3 py-2">
                  All contacts in "<strong>{selGroup?.name}</strong>" will receive this SMS.
                </p>
              )}
              {groups.length === 0 && (
                <p className="text-xs text-slate-400 text-center py-4">No groups yet — create them in the Contacts tab</p>
              )}
            </div>
          )}

          {form.recipient_type === 'contacts' && (
            <div className="space-y-1 max-h-72 overflow-y-auto">
              <div className="flex justify-between items-center mb-1">
                <span className="text-xs text-slate-500">{selected.length} selected</span>
                <button onClick={() => setSelected(contacts.map(c=>c.id))} className="text-xs text-blue-600 font-semibold">All</button>
              </div>
              {contacts.map(c => (
                <label key={c.id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 cursor-pointer">
                  <input type="checkbox" checked={selected.includes(c.id)} onChange={() => toggle(c.id)} className="rounded" />
                  <div><p className="text-sm font-medium text-slate-700">{c.name || 'Unnamed'}</p><p className="text-xs font-mono text-slate-400">{c.phone}</p></div>
                </label>
              ))}
              {contacts.length === 0 && <p className="text-xs text-slate-400 text-center py-4">No contacts. Add some or import from students.</p>}
            </div>
          )}

          {form.recipient_type === 'classes' && (
            <div className="space-y-3">
              <div className="flex rounded-lg bg-slate-100 p-0.5 gap-0.5">
                {[['parents','Parents'],['students','Students']].map(([v,l]) => (
                  <button key={v} onClick={() => setForm(f => ({...f, target: v}))}
                    className={`flex-1 py-1 rounded-md text-xs font-bold transition-all ${form.target===v?'bg-white shadow text-slate-800':'text-slate-500'}`}>
                    {l}
                  </button>
                ))}
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-slate-500">{selected.length} classes</span>
                <button onClick={() => setSelected(classes.map(c=>c.class_name))} className="text-xs text-blue-600 font-semibold">All</button>
              </div>
              <div className="space-y-1 max-h-60 overflow-y-auto">
                {classes.map(c => (
                  <label key={c.class_name} className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 cursor-pointer">
                    <input type="checkbox" checked={selected.includes(c.class_name)} onChange={() => toggle(c.class_name)} className="rounded" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-700">{c.class_name}</p>
                      <p className="text-xs text-slate-400">{form.target==='parents'?c.parent_phone_count:c.student_count} {form.target} w/ phones</p>
                    </div>
                  </label>
                ))}
                {classes.length === 0 && <p className="text-xs text-slate-400 text-center py-4">No classes found</p>}
              </div>
            </div>
          )}

          {form.recipient_type === 'manual' && (
            <div>
              <textarea value={manualNums} onChange={e => setManualNums(e.target.value)}
                rows={7} placeholder={"One number per line:\n08012345678\n08087654321"}
                className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm font-mono outline-none focus:border-blue-400 resize-none" />
              <p className="text-xs text-slate-400 mt-1">{manualNums.split('\n').filter(l=>l.trim()).length} numbers</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── CONTACTS TAB — shared component ──────────────────────────────────────
function ContactsTab() {
  return (
    <SMSContactsTab
      contactsApiBase="/school-sms"
      importStudentsPath="/school-sms/contacts/import-students"
    />
  );
}

// ── CAMPAIGNS TAB ────────────────────────────────────────────────────────
function CampaignsTab() {
  const [campaigns, setCampaigns] = useState([]);
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(1);
  const [loading, setLoading]     = useState(false);
  const [refreshing, setRefreshing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/school-sms/campaigns?page=${page}&limit=10`);
      setCampaigns(r.data || []);
      setTotal(r.total || 0);
    } finally { setLoading(false); }
  }, [page]);

  useEffect(() => { load(); }, [load]);
  const totalPages = Math.max(1, Math.ceil(total / 10));

  const refreshDelivery = async (id) => {
    setRefreshing(id);
    try {
      const r = await api.get(`/school-sms/campaigns/${id}/refresh`);
      if (r.data) setCampaigns(cs => cs.map(c => c.id === id ? {...c, stats: r.data.stats} : c));
      toast.success('Delivery stats updated');
    } catch { toast.error('Failed to refresh'); }
    finally { setRefreshing(null); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">{total} campaigns</p>
        <button onClick={load} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg"><RefreshCw className="w-4 h-4" /></button>
      </div>
      {loading ? <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-slate-300" /></div>
      : campaigns.length === 0 ? <div className="text-center py-16 text-slate-400">No campaigns yet</div>
      : (
        <div className="space-y-3">
          {campaigns.map(c => (
            <div key={c.id} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <p className="font-bold text-slate-800">{c.name}</p>
                  <p className="text-xs text-slate-400">{c.sent_at ? new Date(c.sent_at).toLocaleString('en-NG') : c.scheduled_at ? `Scheduled: ${new Date(c.scheduled_at).toLocaleString('en-NG')}` : 'Pending'}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${STATUS_COLORS[c.status] || 'bg-slate-100 text-slate-500'}`}>{c.status}</span>
                  <button onClick={() => refreshDelivery(c.id)} title="Refresh delivery stats"
                    className="p-1.5 text-slate-400 hover:text-blue-500 hover:bg-blue-50 rounded-lg"
                    data-testid={`refresh-campaign-${c.id}`}>
                    {refreshing === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
              <p className="text-sm text-slate-600 bg-slate-50 rounded-xl px-4 py-2.5 mb-3 line-clamp-2">{c.message}</p>
              <div className="flex items-center gap-4 text-xs">
                <span className="flex items-center gap-1 text-blue-600 font-semibold"><Send className="w-3 h-3" /> {c.stats?.sent ?? 0} Sent</span>
                <span className="flex items-center gap-1 text-emerald-600 font-semibold"><CheckCircle className="w-3 h-3" /> {c.stats?.delivered ?? 0} Delivered</span>
                <span className="flex items-center gap-1 text-red-500 font-semibold"><XCircle className="w-3 h-3" /> {c.stats?.failed ?? 0} Failed</span>
                {c.credits_used && <span className="ml-auto text-slate-400">{c.credits_used} credits</span>}
              </div>
            </div>
          ))}
        </div>
      )}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button onClick={() => setPage(p=>Math.max(1,p-1))} disabled={page===1} className="p-2 rounded-xl border border-slate-200 disabled:opacity-40"><ChevronLeft className="w-4 h-4" /></button>
          <span className="text-sm">{page}/{totalPages}</span>
          <button onClick={() => setPage(p=>Math.min(totalPages,p+1))} disabled={page===totalPages} className="p-2 rounded-xl border border-slate-200 disabled:opacity-40"><ChevronRight className="w-4 h-4" /></button>
        </div>
      )}
    </div>
  );
}

// ── AUTOMATIONS TAB ───────────────────────────────────────────────────────
function AutomationsTab() {
  const [config, setConfig]   = useState(null);
  const [saving, setSaving]   = useState(false);
  const [running, setRunning] = useState(null);

  const load = () => api.get('/school-sms/automations').then(r => setConfig(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try { await api.put('/school-sms/automations', config); toast.success('Saved'); }
    catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const runNow = async (type) => {
    setRunning(type);
    try {
      const r = await api.post('/school-sms/automations/run-now', { type });
      toast.success(r.message || 'Done');
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    finally { setRunning(null); }
  };

  if (!config) return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-slate-300" /></div>;

  const toggle = (key) => setConfig(c => ({...c, [key]: !c[key]}));

  return (
    <div className="max-w-2xl space-y-6">
      {/* Birthday SMS */}
      <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-bold text-slate-800">Birthday SMS</h3>
            <p className="text-xs text-slate-500 mt-0.5">Automatically send birthday wishes to students' parents on their birthday</p>
            {config.birthday_last_run_at && (
              <p className="text-xs text-emerald-600 mt-1">Last run: {new Date(config.birthday_last_run_at).toLocaleString('en-NG')} · {config.birthday_last_sent_count ?? 0} sent</p>
            )}
          </div>
          <button onClick={() => toggle('birthday_enabled')} className={`w-12 h-6 rounded-full transition-colors relative flex-shrink-0 ${config.birthday_enabled ? 'bg-emerald-500' : 'bg-slate-300'}`} data-testid="birthday-toggle">
            <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${config.birthday_enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
          </button>
        </div>
        {config.birthday_enabled && (
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Birthday Message</label>
              <textarea value={config.birthday_message ?? ''} onChange={e => setConfig(c => ({...c, birthday_message: e.target.value}))}
                rows={3} placeholder="Happy Birthday {name}! Wishing you a wonderful day from {school}."
                className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-blue-400 resize-none" />
              <p className="text-xs text-slate-400 mt-1">Variables: <code>{'{'+'name}'}</code> · <code>{'{'+'school}'}</code></p>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Send Time (daily)</label>
              <input type="time" value={config.birthday_time ?? '08:00'} onChange={e => setConfig(c => ({...c, birthday_time: e.target.value}))}
                className="border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-blue-400" />
            </div>
            <button onClick={() => runNow('birthday')} disabled={running === 'birthday'}
              className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-xl text-sm font-semibold hover:bg-slate-200 disabled:opacity-40"
              data-testid="run-birthday-now-btn">
              {running === 'birthday' ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Run Now (Test Today's Birthdays)
            </button>
          </div>
        )}
      </div>

      {/* Fee Reminder */}
      <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-bold text-slate-800">Fee Payment Reminder</h3>
            <p className="text-xs text-slate-500 mt-0.5">Automatically remind parents about upcoming fee due dates</p>
            {config.fee_last_run_at && (
              <p className="text-xs text-emerald-600 mt-1">Last run: {new Date(config.fee_last_run_at).toLocaleString('en-NG')} · {config.fee_last_sent_count ?? 0} sent</p>
            )}
          </div>
          <button onClick={() => toggle('fee_reminder_enabled')} className={`w-12 h-6 rounded-full transition-colors relative flex-shrink-0 ${config.fee_reminder_enabled ? 'bg-emerald-500' : 'bg-slate-300'}`} data-testid="fee-reminder-toggle">
            <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${config.fee_reminder_enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
          </button>
        </div>
        {config.fee_reminder_enabled && (
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Reminder Message</label>
              <textarea value={config.fee_reminder_message ?? ''} onChange={e => setConfig(c => ({...c, fee_reminder_message: e.target.value}))}
                rows={3} placeholder="Dear {parent}, {name}'s fees of ₦{amount} is due in {days} day(s)."
                className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-blue-400 resize-none" />
              <p className="text-xs text-slate-400 mt-1">Variables: <code>{'{'+'parent}'}</code> · <code>{'{'+'name}'}</code> · <code>{'{'+'amount}'}</code> · <code>{'{'+'days}'}</code></p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Days Before Due</label>
                <input type="number" min="1" value={config.fee_reminder_days_before ?? 3} onChange={e => setConfig(c => ({...c, fee_reminder_days_before: parseInt(e.target.value)}))}
                  className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-blue-400" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Send Time (daily)</label>
                <input type="time" value={config.fee_reminder_time ?? '09:00'} onChange={e => setConfig(c => ({...c, fee_reminder_time: e.target.value}))}
                  className="border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-blue-400" />
              </div>
            </div>
            <button onClick={() => runNow('fee_reminder')} disabled={running === 'fee_reminder'}
              className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-xl text-sm font-semibold hover:bg-slate-200 disabled:opacity-40"
              data-testid="run-fee-now-btn">
              {running === 'fee_reminder' ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Run Now (Test Fee Reminders)
            </button>
          </div>
        )}
      </div>

      <button onClick={save} disabled={saving}
        className="w-full py-3 rounded-xl bg-[#2563eb] text-white font-bold text-sm hover:bg-blue-700 disabled:opacity-40 transition-all flex items-center justify-center gap-2"
        data-testid="save-automations-btn">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
        Save Automation Settings
      </button>

      {/* Overdue Fee Reminder */}
      <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-bold text-slate-800">Overdue Fee Reminder</h3>
            <p className="text-xs text-slate-500 mt-0.5">Send reminders to parents whose fees are already past due</p>
            {config.fee_overdue_last_run_at && (
              <p className="text-xs text-amber-600 mt-1">Last run: {new Date(config.fee_overdue_last_run_at).toLocaleString('en-NG')} · {config.fee_overdue_last_sent_count ?? 0} sent</p>
            )}
          </div>
          <button onClick={() => toggle('fee_overdue_enabled')} className={`w-12 h-6 rounded-full transition-colors relative flex-shrink-0 ${config.fee_overdue_enabled ? 'bg-amber-500' : 'bg-slate-300'}`} data-testid="fee-overdue-toggle">
            <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${config.fee_overdue_enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
          </button>
        </div>
        {config.fee_overdue_enabled && (
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Overdue Message</label>
              <textarea value={config.fee_overdue_message ?? ''} onChange={e => setConfig(c => ({...c, fee_overdue_message: e.target.value}))}
                rows={3} placeholder="Dear {parent}, {name}'s fees of ₦{amount} is {days_overdue} day(s) overdue. Please settle urgently."
                className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-blue-400 resize-none"
                data-testid="fee-overdue-message" />
              <p className="text-xs text-slate-400 mt-1">Variables: <code>{'{'+'parent}'}</code> · <code>{'{'+'name}'}</code> · <code>{'{'+'amount}'}</code> · <code>{'{'+'days_overdue}'}</code></p>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Send Every (days)</label>
                <input type="number" min="1" max="30" value={config.fee_overdue_cadence_days ?? 1} onChange={e => setConfig(c => ({...c, fee_overdue_cadence_days: parseInt(e.target.value)}))}
                  className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-blue-400"
                  data-testid="fee-overdue-cadence" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Send Time</label>
                <input type="time" value={config.fee_overdue_time ?? '09:30'} onChange={e => setConfig(c => ({...c, fee_overdue_time: e.target.value}))}
                  className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-blue-400" />
              </div>
              <div className="flex items-end pb-0.5">
                <button onClick={() => runNow('fee_overdue')} disabled={running === 'fee_overdue'}
                  className="w-full flex items-center justify-center gap-1.5 px-3 py-2.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-xl text-xs font-semibold hover:bg-amber-100 disabled:opacity-40"
                  data-testid="run-fee-overdue-now-btn">
                  {running === 'fee_overdue' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                  Run Now
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── WALLET TAB ────────────────────────────────────────────────────────────
function WalletTab({ pricing }) {
  const [wallet, setWallet]   = useState(null);
  const [txns, setTxns]       = useState([]);
  const [units, setUnits]     = useState('');
  const [topping, setTopping] = useState(false);

  const load = useCallback(async () => {
    api.get('/school-sms/wallet').then(r => {
      setWallet(r.data?.wallet);
      setTxns(r.data?.transactions || []);
    }).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const pricePerCredit = pricing?.price_per_credit_naira ?? 2;
  const txFee          = pricing?.transaction_fee_naira ?? 0;
  const minUnits       = pricing?.min_topup_units ?? 100;
  const unitsNum       = parseInt(units) || 0;
  const subtotal       = unitsNum * pricePerCredit;
  const totalPay       = subtotal + txFee;

  const topup = async () => {
    if (!unitsNum || unitsNum < minUnits) return toast.error(`Minimum topup is ${minUnits} credits`);
    setTopping(true);
    try {
      const callbackUrl = `${window.location.origin}/school/sms?topup=success`;
      const r = await api.post('/school-sms/wallet/topup/initialize', { units: unitsNum, callback_url: callbackUrl });
      toast.success(`Redirecting to payment for ${r.data?.credits} credits…`);
      window.location.href = r.data?.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to initialize payment');
    } finally { setTopping(false); }
  };

  return (
    <div className="max-w-2xl space-y-6">
      {/* Balance card */}
      <div className="bg-gradient-to-br from-[#2563eb] to-violet-600 rounded-3xl p-6 text-white">
        <p className="text-sm font-bold text-blue-200 uppercase tracking-wide">SMS Credit Balance</p>
        <p className="text-5xl font-extrabold mt-1">{wallet?.balance ?? 0}</p>
        <p className="text-blue-200 text-sm mt-1">credits available</p>
        <div className="mt-4 flex gap-4 text-xs text-blue-200">
          <span>Total topped up: {wallet?.total_topped_up ?? 0} units</span>
          <span>·</span>
          <span>Total spent: {wallet?.total_spent ?? 0} units</span>
        </div>
      </div>

      {/* Topup form */}
      <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 space-y-4">
        <h3 className="font-bold text-slate-800">Top Up Credits</h3>
        <div>
          <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Number of units</label>
          <input type="number" min={minUnits} step="1" value={units} onChange={e => setUnits(e.target.value)}
            placeholder={`e.g. ${minUnits}`}
            className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-blue-400"
            data-testid="topup-units-input" />
        </div>

        {unitsNum > 0 && (
          <div className="bg-slate-50 rounded-2xl p-4 space-y-2 text-sm border border-slate-100">
            <div className="flex justify-between"><span className="text-slate-500">Unit price</span><span className="font-semibold text-slate-800">₦{pricePerCredit.toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Amount to pay</span><span className="font-semibold text-slate-800">₦{subtotal.toFixed(2)}</span></div>
            {txFee > 0 && <div className="flex justify-between"><span className="text-slate-500">Transaction fee</span><span className="font-semibold text-slate-800">₦{txFee.toFixed(2)}</span></div>}
            <div className="flex justify-between pt-2 border-t border-slate-200">
              <span className="font-bold text-slate-800">Total payment</span>
              <span className="font-bold text-[#2563eb] text-base">₦{totalPay.toFixed(2)}</span>
            </div>
          </div>
        )}

        <button onClick={topup} disabled={topping || !unitsNum || unitsNum < minUnits}
          className="w-full py-3 rounded-xl bg-emerald-500 text-white font-bold hover:bg-emerald-600 disabled:opacity-40 transition-all flex items-center justify-center gap-2"
          data-testid="topup-btn">
          {topping ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
          Pay ₦{unitsNum > 0 ? totalPay.toFixed(2) : '—'} for {unitsNum || 0} credits
        </button>
        <p className="text-xs text-slate-400 text-center">Minimum: {minUnits} credits · ₦{(minUnits * pricePerCredit + txFee).toFixed(2)} total</p>
      </div>

      {/* Transactions */}
      {txns.length > 0 && (
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="px-5 py-3.5 border-b border-slate-50 font-bold text-slate-700 text-sm">Transaction History</div>
          <div className="divide-y divide-slate-50">
            {txns.map(t => (
              <div key={t.id} className="flex items-center justify-between px-5 py-3.5">
                <div>
                  <p className="text-sm font-medium text-slate-700">{t.description}</p>
                  <p className="text-xs text-slate-400">{new Date(t.created_at).toLocaleString('en-NG')}</p>
                </div>
                <span className={`text-sm font-bold ${t.type==='topup'?'text-emerald-600':' text-red-500'}`}>
                  {t.type==='topup'?'+':'-'}{t.units} credits
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── SETTINGS TAB ─────────────────────────────────────────────────────────
function SettingsTab() {
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    api.get('/school-sms/automations').then(r => setConfig(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/school-sms/automations', { sender_name: config?.sender_name });
      toast.success('Sender name saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const sendTestSMS = async () => {
    if (!testPhone.trim()) { toast.error('Enter a phone number'); return; }
    setTesting(true);
    try {
      const r = await api.post('/school-sms/test-sender', { phone: testPhone });
      toast.success(r.message || 'Test SMS sent successfully');
      setTestPhone('');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Test SMS failed'); }
    finally { setTesting(false); }
  };

  return (
    <div className="max-w-md space-y-4">
      {/* Sender Name */}
      <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 space-y-4">
        <h3 className="font-bold text-slate-800">SMS Sender Name</h3>
        <p className="text-xs text-slate-500">This name appears on the recipient's phone when they receive your SMS. Max 11 characters.</p>
        <input value={config?.sender_name ?? ''} onChange={e => setConfig(c => ({...c, sender_name: e.target.value.slice(0,11)}))}
          placeholder="e.g. MySchool" maxLength={11}
          className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-blue-400 font-mono" />
        <p className="text-xs text-slate-400">{(config?.sender_name||'').length}/11 characters</p>
        <button onClick={save} disabled={saving}
          className="w-full py-2.5 rounded-xl bg-[#2563eb] text-white font-bold text-sm hover:bg-blue-700 disabled:opacity-40 transition-all"
          data-testid="save-sender-name-btn">
          {saving ? 'Saving…' : 'Save Sender Name'}
        </button>
      </div>

      {/* Test Sender */}
      <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 space-y-4">
        <div>
          <h3 className="font-bold text-slate-800">Test Sender Name</h3>
          <p className="text-xs text-slate-500 mt-1">Send a real test SMS to confirm your sender name appears correctly on delivery. Costs 1 credit.</p>
        </div>
        <div className="bg-slate-50 rounded-xl px-4 py-2.5 flex items-center justify-between">
          <span className="text-xs text-slate-500">Current sender name</span>
          <span className="font-mono font-bold text-sm text-[#2563eb]">{config?.sender_name || 'Eduva SMS'}</span>
        </div>
        <input
          value={testPhone} onChange={e => setTestPhone(e.target.value)}
          placeholder="Enter phone number (e.g. 08012345678)"
          className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-blue-400"
          data-testid="test-sender-phone"
        />
        <button onClick={sendTestSMS} disabled={testing || !testPhone.trim()}
          className="w-full py-2.5 rounded-xl bg-emerald-500 text-white font-bold text-sm hover:bg-emerald-600 disabled:opacity-40 transition-all flex items-center justify-center gap-2"
          data-testid="send-test-sms-btn">
          {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {testing ? 'Sending Test…' : 'Send Test SMS'}
        </button>
      </div>
    </div>
  );
}

// ── MAIN PAGE ─────────────────────────────────────────────────────────────
export default function SchoolSMSPlatformPage() {
  const [tab, setTab]           = useState('dashboard');
  const [accessData, setAccessData] = useState(null);
  const [loading, setLoading]   = useState(true);

  const loadAccess = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/school-sms/access-status');
      setAccessData(r.data);
    } catch {
      setAccessData(null);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAccess(); }, [loadAccess]);

  // Check topup success param
  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.get('topup') === 'success') {
      const ref = url.searchParams.get('reference');
      if (ref) {
        api.post('/school-sms/wallet/topup/verify', { reference: ref })
           .then(r => { toast.success(r.message || 'Credits added!'); loadAccess(); })
           .catch(() => {});
      }
    }
  }, [loadAccess]);

  const requestAccess = async () => {
    await api.post('/school-sms/request-access');
    toast.success('Access request submitted!');
    loadAccess();
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-300" /></div>;

  const hasAccess = accessData?.has_access;
  const reqStatus = accessData?.access_request?.status;

  return (
    <div className="px-6 md:px-8 py-6 md:py-8" data-testid="school-sms-page">
      {/* Dark hero header */}
      <div className="relative bg-[#0f172a] rounded-3xl overflow-hidden mb-7" style={{ minHeight: 120 }}>
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '28px 28px' }} />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-500/10 rounded-full blur-3xl -translate-x-1/4 translate-y-1/3" />
        <div className="relative p-7 flex items-center gap-4">
          <div className="w-11 h-11 rounded-2xl bg-white/10 flex items-center justify-center">
            <MessageSquareText className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-extrabold text-white tracking-tight">SMS Platform</h1>
            <p className="text-slate-400 text-sm mt-0.5">Send bulk SMS to parents, students and staff</p>
          </div>
          {hasAccess && accessData?.wallet && (
            <div className="ml-auto flex items-center gap-2 bg-white/10 rounded-xl px-3 py-2">
              <Wallet className="w-4 h-4 text-white/60" />
              <span className="text-white font-bold text-sm">{accessData.wallet.balance} credits</span>
            </div>
          )}
        </div>
      </div>

      {!hasAccess ? (
        <AccessGate status={reqStatus} onRequest={requestAccess} />
      ) : (
        <>
          {/* Tab nav */}
          <div className="bg-slate-100 rounded-2xl p-1 flex gap-1 mb-7 overflow-x-auto">
            {TABS.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold whitespace-nowrap transition-all ${tab===t.id?'bg-white text-slate-800 shadow':'text-slate-500 hover:text-slate-700'}`}
                data-testid={`school-sms-tab-${t.id}`}>
                <t.icon className="w-4 h-4" />
                {t.label}
              </button>
            ))}
          </div>

          {tab === 'dashboard'   && <DashboardTab accessData={accessData} />}
          {tab === 'send'        && <SendSMSTab accessData={accessData} />}
          {tab === 'contacts'    && <ContactsTab />}
          {tab === 'campaigns'   && <CampaignsTab />}
          {tab === 'automations' && <AutomationsTab />}
          {tab === 'wallet'      && <WalletTab pricing={accessData?.pricing} />}
          {tab === 'settings'    && <SettingsTab />}
        </>
      )}
    </div>
  );
}
